import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { MessageSquareQuote, Star, Trash2, Upload, Loader2, RefreshCw } from "lucide-react";

/** Admin panel to import & review the reviews for each product. */
export function ReviewsAdminPanel() {
  const { lang } = useLang();
  const [products, setProducts] = useState([]);
  const [selectedSid, setSelectedSid] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [paste, setPaste] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [judgemeStatus, setJudgemeStatus] = useState(null);
  const [judgemeBusy, setJudgemeBusy] = useState(false);

  useEffect(() => {
    api.get("/shopify/products?limit=200").then((r) => {
      const rows = r.data.products || [];
      setProducts(rows);
      if (!selectedSid && rows.length) setSelectedSid(rows[0].shopify_id);
    });
    api.get("/reviews/judgeme/status")
      .then((r) => setJudgemeStatus(r.data))
      .catch(() => setJudgemeStatus({ configured: false }));
  }, []);

  const loadReviews = async (sid) => {
    if (!sid) return;
    const { data } = await api.get(`/reviews/products/${sid}`);
    setReviews(data.reviews || []);
  };

  useEffect(() => { loadReviews(selectedSid); }, [selectedSid]);

  const importJson = async () => {
    if (!paste.trim() || !selectedSid) return;
    setBusy(true);
    try {
      let parsed;
      try { parsed = JSON.parse(paste); } catch { throw new Error("Invalid JSON"); }
      const reviewsList = Array.isArray(parsed) ? parsed : parsed.reviews;
      if (!Array.isArray(reviewsList)) throw new Error("Expected an array or {reviews:[]}");
      const { data } = await api.post(`/reviews/products/${selectedSid}/import`, { reviews: reviewsList });
      toast.success(`${data.imported} ${t(lang, "reviews_imported")}`);
      setPaste("");
      await loadReviews(selectedSid);
    } catch (err) {
      toast.error(err.message || err?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const importCsv = async () => {
    if (!file || !selectedSid) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/reviews/products/${selectedSid}/import-csv`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} ${t(lang, "reviews_imported")}`);
      setFile(null);
      await loadReviews(selectedSid);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV upload failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (rid) => {
    await api.delete(`/reviews/${rid}`);
    await loadReviews(selectedSid);
  };

  const syncJudgeme = async () => {
    if (!judgemeStatus?.configured || judgemeBusy) {
      if (!judgemeStatus?.configured) toast.error(t(lang, "reviews_judgeme_not_configured"));
      return;
    }
    setJudgemeBusy(true);
    try {
      const { data } = await api.post("/reviews/judgeme/sync");
      toast.success(`${data.imported} ${t(lang, "reviews_judgeme_synced")}`);
      await loadReviews(selectedSid);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Judge.me sync failed");
    } finally {
      setJudgemeBusy(false);
    }
  };

  return (
    <div className="hq-card overflow-hidden" data-testid="reviews-admin-panel">
      <div className="p-5 border-b border-white/10 flex items-center gap-2 flex-wrap">
        <MessageSquareQuote size={14} className="text-[color:var(--hq-cyan)]" />
        <div className="font-display text-lg">{t(lang, "reviews_admin_title")}</div>
        {judgemeStatus?.configured && (
          <button
            data-testid="reviews-judgeme-sync-btn"
            onClick={syncJudgeme}
            disabled={judgemeBusy}
            className="hq-btn hq-btn-cyan text-xs disabled:opacity-50 ml-2"
          >
            {judgemeBusy ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
            {judgemeBusy ? t(lang, "reviews_judgeme_syncing") : t(lang, "reviews_judgeme_sync")}
          </button>
        )}
        <span className="ml-auto font-mono text-[10px] uppercase text-white/40">
          {t(lang, "reviews_admin_disclaimer")}
        </span>
      </div>

      <div className="p-5 grid gap-4 md:grid-cols-3 border-b border-white/10">
        <label className="text-xs">
          <div className="font-mono uppercase text-[10px] text-white/40 mb-1">{t(lang, "reviews_product")}</div>
          <select
            data-testid="reviews-product-select"
            value={selectedSid || ""}
            onChange={(e) => setSelectedSid(Number(e.target.value))}
            className="hq-input w-full"
          >
            {products.map((p) => (
              <option key={p.shopify_id} value={p.shopify_id}>{p.title}</option>
            ))}
          </select>
        </label>
        <div className="md:col-span-2">
          <div className="font-mono uppercase text-[10px] text-white/40 mb-1">{t(lang, "reviews_paste_json")}</div>
          <textarea
            data-testid="reviews-json-input"
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            className="hq-input w-full h-32 font-mono text-xs"
            placeholder='[{"author":"Anna S.","rating":5,"body":"Great product…","source":"aliexpress","source_url":"https://…","verified_purchase":true,"country":"CH"}]'
          />
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button
              data-testid="reviews-import-btn"
              onClick={importJson}
              disabled={busy || !paste.trim() || !selectedSid}
              className="hq-btn hq-btn-primary text-xs disabled:opacity-50"
            >
              {busy ? <Loader2 size={10} className="animate-spin" /> : null}
              {t(lang, "reviews_import_json")}
            </button>
            <input
              data-testid="reviews-csv-input"
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-xs text-white/60"
            />
            <button
              data-testid="reviews-csv-btn"
              onClick={importCsv}
              disabled={busy || !file || !selectedSid}
              className="hq-btn hq-btn-ghost text-xs disabled:opacity-50"
            >
              <Upload size={10} /> {t(lang, "reviews_import_csv")}
            </button>
          </div>
        </div>
      </div>

      <table className="hq-table">
        <thead>
          <tr>
            <th></th><th>{t(lang, "reviews_col_author")}</th><th>{t(lang, "reviews_col_rating")}</th>
            <th>{t(lang, "reviews_col_body")}</th><th>{t(lang, "reviews_col_source")}</th><th></th>
          </tr>
        </thead>
        <tbody>
          {reviews.map((r) => (
            <tr key={r.review_id} data-testid={`reviews-row-${r.review_id}`}>
              <td>{r.verified_purchase && <span title="verified" className="hq-dot hq-dot-green" />}</td>
              <td className="text-xs">{r.author}</td>
              <td className="font-mono text-xs flex items-center gap-1"><Star size={10} className="fill-[color:var(--hq-cyan)] text-[color:var(--hq-cyan)]"/>{r.rating}</td>
              <td className="text-xs text-white/70 max-w-[420px] truncate">{r.body}</td>
              <td className="font-mono text-[10px] uppercase text-white/40">{r.source}</td>
              <td className="text-right">
                <button
                  data-testid={`reviews-del-${r.review_id}`}
                  onClick={() => del(r.review_id)}
                  className="hq-btn hq-btn-ghost text-xs"
                >
                  <Trash2 size={10} />
                </button>
              </td>
            </tr>
          ))}
          {reviews.length === 0 && (
            <tr><td colSpan={6} className="text-center py-8 font-mono text-xs text-white/40">
              {t(lang, "reviews_empty")}
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
